export const CHARGE_FIELD_CONFIG = {
  KWH_FIXED_CHARGE: {
    label: 'kWh Fixed Charge',
    chargeType: 'unit',
    lineKey: 'kwhFixed',
    unit: 'kWh',
  },
  KVA_DEMAND_CHARGE: {
    label: 'Horse Power Charge / KVA Demand Charge',
    chargeType: 'demand',
    lineKey: 'kvaDemand',
    unit: 'kVA',
  },
  KVA_PER_UNIT_CHARGE: {
    label: 'Horse Power Charge / KVA Demand Charge',
    chargeType: 'demand',
    lineKey: 'kvaDemand',
    unit: 'kVA',
  },
  SERVICE_CHARGE: {
    label: 'Meter Service Charge',
    chargeType: 'fixed',
  },
  ADMINISTRATION_FEES: {
    label: 'Administration Fees',
    chargeType: 'percentage',
  },
  COMMERCIAL_TAX: {
    label: 'Commercial Tax',
    chargeType: 'percentage',
  },
  ON_PEAK_UNIT_CHARGE: {
    label: 'On Peak Unit Charge',
    chargeType: 'unit',
    lineKey: 'onPeak',
    unit: 'kWh',
    defaultRate: 4.1025,
  },
  OFF_PEAK_UNIT_CHARGE: {
    label: 'Off Peak Unit Charge',
    chargeType: 'unit',
    lineKey: 'offPeak',
    unit: 'kWh',
    defaultRate: 2.5849,
  },
  SUNDAY_UNIT_CHARGE: {
    label: 'Sunday Unit Charge',
    chargeType: 'unit',
    lineKey: 'sunday',
    unit: 'kWh',
    defaultRate: 2.5849,
  },
  MISC_UNIT_CHARGE: {
    label: 'Misc Unit Charge',
    chargeType: 'unit',
    lineKey: 'misc',
    unit: 'kWh',
    defaultRate: 3.3437,
  },
  ON_PEAK_DEMAND_CHARGE: {
    label: 'On Peak Demand Charge',
    chargeType: 'demand',
    lineKey: 'onPeak',
    unit: 'kW',
  },
  OFF_PEAK_DEMAND_CHARGE: {
    label: 'Off Peak Demand Charge',
    chargeType: 'demand',
    lineKey: 'offPeak',
    unit: 'kW',
  },
  SUNDAY_DEMAND_CHARGE: {
    label: 'Sunday Demand Charge',
    chargeType: 'demand',
    lineKey: 'sunday',
    unit: 'kW',
  },
  OIL_SURCHARGE: {
    label: 'Oil Surcharge',
    chargeType: 'unit',
    lineKey: 'oilSurcharge',
    unit: 'kWh',
  },
};

export const TIME_OF_USE_UNIT_CODES = [
  'ON_PEAK_UNIT_CHARGE',
  'OFF_PEAK_UNIT_CHARGE',
  'SUNDAY_UNIT_CHARGE',
  'MISC_UNIT_CHARGE',
];

export const TIME_OF_USE_DEMAND_CODES = [
  'ON_PEAK_DEMAND_CHARGE',
  'OFF_PEAK_DEMAND_CHARGE',
  'SUNDAY_DEMAND_CHARGE',
];

export const TIME_OF_USE_BASE_CODES = [
  ...TIME_OF_USE_UNIT_CODES,
  ...TIME_OF_USE_DEMAND_CODES,
  'OIL_SURCHARGE',
  'SERVICE_CHARGE',
];

export const TIME_OF_USE_MODE_TRIGGER_CODES = [
  ...TIME_OF_USE_UNIT_CODES,
  ...TIME_OF_USE_DEMAND_CODES,
  'OIL_SURCHARGE',
];

export const STANDARD_TARIFF_BASE_CODES = [
  'KWH_FIXED_CHARGE',
  'KVA_DEMAND_CHARGE',
  'KVA_PER_UNIT_CHARGE',
  'SERVICE_CHARGE',
];

export const STANDARD_TARIFF_PERCENTAGE_CODES = [
  'ADMINISTRATION_FEES',
  'COMMERCIAL_TAX',
  'VAT',
  'REA',
  'EWURA',
];

export function getChargeCategoryCode(record = {}) {
  return record.category_code || record.categoryCode || record.code || '';
}

export function getChargeCategoryOrder(record = {}) {
  const rawOrder = record
    .order ?? record.calculation_order ?? record.sort_order ?? Number.MAX_SAFE_INTEGER;
  const parsed = Number(rawOrder);
  return Number.isFinite(parsed) ? parsed : Number.MAX_SAFE_INTEGER;
}

export function isChargeCategoryActive(record = {}) {
  const hasStatus = Object.prototype.hasOwnProperty.call(record, 'status');
  const statusActive = !hasStatus || String(record.status).toLowerCase() === 'active';

  const hasIsActive = Object.prototype.hasOwnProperty.call(record, 'is_active');
  const isActive = !hasIsActive || record.is_active === true;

  return statusActive && isActive;
}

export function normalizeActiveChargeCategories(records = []) {
  return (Array.isArray(records) ? records : [])
    .filter((record) => record && isChargeCategoryActive(record))
    .sort((a, b) => getChargeCategoryOrder(a) - getChargeCategoryOrder(b));
}

export function getActiveChargeCodes(records = []) {
  return normalizeActiveChargeCategories(records)
    .map(getChargeCategoryCode)
    .filter(Boolean);
}

export function hasChargeCode(records = [], code = '') {
  return getActiveChargeCodes(records).includes(code);
}

export function hasAnyChargeCode(records = [], codes = []) {
  const activeCodes = new Set(getActiveChargeCodes(records));
  return codes.some((code) => activeCodes.has(code));
}

function formatChargeLabel(code = '') {
  return String(code || '')
    .toLowerCase()
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

function toLineKey(code = '') {
  const words = String(code || '')
    .toLowerCase()
    .replace(/_charge$/, '')
    .split('_')
    .filter(Boolean);

  return words.map((word, index) => (
    index === 0 ? word : word.charAt(0).toUpperCase() + word.slice(1)
  )).join('');
}

function inferChargeDefinition(code = '') {
  if (!code) return null;
  if (code.endsWith('_UNIT_CHARGE')) {
    return {
      label: formatChargeLabel(code),
      chargeType: 'unit',
      lineKey: toLineKey(code),
      unit: 'kWh',
    };
  }

  if (code.endsWith('_DEMAND_CHARGE')) {
    return {
      label: formatChargeLabel(code),
      chargeType: 'demand',
      lineKey: toLineKey(code),
      unit: 'kW',
    };
  }

  return null;
}

export function getChargeDefinition(code = '') {
  return CHARGE_FIELD_CONFIG[code] || inferChargeDefinition(code);
}

export function buildTimeOfUseUnitLines(records = []) {
  return normalizeActiveChargeCategories(records)
    .map((category) => {
      const code = getChargeCategoryCode(category);
      const config = getChargeDefinition(code);
      if (
        !config
        || config.chargeType !== 'unit'
        || code === 'OIL_SURCHARGE'
        || (!TIME_OF_USE_UNIT_CODES.includes(code) && !code.endsWith('_UNIT_CHARGE'))
      ) {
        return null;
      }

      return {
        key: config.lineKey,
        categoryCode: code,
        label: config.label,
        rate: config.defaultRate || 0,
      };
    })
    .filter(Boolean);
}

export function buildTimeOfUseDemandLines(records = []) {
  return normalizeActiveChargeCategories(records)
    .map((category) => {
      const code = getChargeCategoryCode(category);
      const config = getChargeDefinition(code);
      if (
        !config
        || config.chargeType !== 'demand'
        || (
          !TIME_OF_USE_DEMAND_CODES.includes(code)
          && (!code.endsWith('_DEMAND_CHARGE') || code === 'KVA_DEMAND_CHARGE')
        )
      ) {
        return null;
      }

      return {
        key: config.lineKey,
        categoryCode: code,
        label: config.label,
        rate: 0,
      };
    })
    .filter(Boolean);
}

export function buildStandardTariffChargeLines(records = []) {
  const standardTariffCodes = [
    ...STANDARD_TARIFF_BASE_CODES,
    ...STANDARD_TARIFF_PERCENTAGE_CODES,
  ];

  return normalizeActiveChargeCategories(records)
    .map((category) => {
      const code = getChargeCategoryCode(category);
      const config = getChargeDefinition(code);
      if (!config || !standardTariffCodes.includes(code)) {
        return null;
      }

      return {
        categoryCode: code,
        label: config.label,
        chargeType: config.chargeType,
        lineKey: config.lineKey || null,
        unit: config.unit || '',
      };
    })
    .filter(Boolean);
}

export function getDefaultPercentageDependencies(categoryCode = '') {
  if (categoryCode === 'ADMINISTRATION_FEES') {
    return ['KWH_FIXED_CHARGE', 'KVA_DEMAND_CHARGE', 'KVA_PER_UNIT_CHARGE', 'SERVICE_CHARGE'];
  }

  if (categoryCode === 'COMMERCIAL_TAX') {
    return ['ADMINISTRATION_FEES'];
  }

  return [];
}

export function normalizePercentageAppliesTo(value) {
  if (Array.isArray(value)) {
    return value.filter(Boolean);
  }

  if (typeof value !== 'string') {
    return [];
  }

  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed.filter(Boolean) : [];
  } catch (error) {
    return value
      .replace(/^\[/, '')
      .replace(/\]$/, '')
      .split(',')
      .map((item) => item.trim().replace(/^["']|["']$/g, ''))
      .filter(Boolean);
  }
}
